import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Group, Modal, Slider, Stack, Text } from '@mantine/core'
import {
	clamp,
	cropImageToBlob,
	getCropGeometry,
	loadImage,
} from '../services/utils/imageCropper'

const ImageCropperModal = ({
	opened,
	onClose,
	sourceUrl,
	title = 'Crop Image',
	targetWidth,
	targetHeight,
	onApply,
	applyLabel = 'Use Crop',
	processing = false,
	zIndex = 3000,
	closeOnClickOutside = true,
	closeOnEscape = true,
}) => {
	const [imageSize, setImageSize] = useState(null)
	const [zoom, setZoom] = useState(1)
	const [pan, setPan] = useState({ x: 0, y: 0 })
	const [applying, setApplying] = useState(false)
	const viewportRef = useRef(null)
	const dragRef = useRef(null)

	useEffect(() => {
		if (!opened || !sourceUrl) {
			setImageSize(null)
			return
		}

		let cancelled = false
		const load = async () => {
			try {
				const image = await loadImage(sourceUrl)
				if (cancelled) return
				setImageSize({
					width: image.naturalWidth || image.width,
					height: image.naturalHeight || image.height,
				})
				setZoom(1)
				setPan({ x: 0, y: 0 })
			} catch (error) {
				if (!cancelled) {
					alert(error.message || 'Could not load selected image')
				}
			}
		}

		load()
		return () => {
			cancelled = true
		}
	}, [opened, sourceUrl])

	const geometry = useMemo(
		() =>
			getCropGeometry({
				imageWidth: imageSize?.width,
				imageHeight: imageSize?.height,
				viewportWidth: targetWidth,
				viewportHeight: targetHeight,
				zoom,
				pan,
			}),
		[imageSize, targetWidth, targetHeight, zoom, pan],
	)

	useEffect(() => {
		if (!geometry) return
		if (geometry.panX === pan.x && geometry.panY === pan.y) return
		setPan({ x: geometry.panX, y: geometry.panY })
	}, [geometry, pan.x, pan.y])

	const imageStyle = useMemo(() => {
		if (!geometry || !targetWidth || !targetHeight) return {}
		const widthPct = (geometry.displayWidth / targetWidth) * 100
		const heightPct = (geometry.displayHeight / targetHeight) * 100
		const leftPct =
			((targetWidth / 2 + geometry.panX - geometry.displayWidth / 2) /
				targetWidth) *
			100
		const topPct =
			((targetHeight / 2 + geometry.panY - geometry.displayHeight / 2) /
				targetHeight) *
			100

		return {
			position: 'absolute',
			left: `${leftPct}%`,
			top: `${topPct}%`,
			width: `${widthPct}%`,
			height: `${heightPct}%`,
			userSelect: 'none',
			touchAction: 'none',
			pointerEvents: 'none',
		}
	}, [geometry, targetWidth, targetHeight])

	const handlePointerDown = (event) => {
		if (!geometry || !viewportRef.current) return
		const rect = viewportRef.current.getBoundingClientRect()
		if (!rect.width || !rect.height) return

		dragRef.current = {
			pointerId: event.pointerId,
			startX: event.clientX,
			startY: event.clientY,
			startPanX: geometry.panX,
			startPanY: geometry.panY,
			virtualPerCssX: targetWidth / rect.width,
			virtualPerCssY: targetHeight / rect.height,
		}

		event.currentTarget.setPointerCapture(event.pointerId)
	}

	const handlePointerMove = (event) => {
		if (!dragRef.current || !geometry) return
		if (dragRef.current.pointerId !== event.pointerId) return

		const dx =
			(event.clientX - dragRef.current.startX) * dragRef.current.virtualPerCssX
		const dy =
			(event.clientY - dragRef.current.startY) * dragRef.current.virtualPerCssY
		const nextPanX = clamp(
			dragRef.current.startPanX + dx,
			-geometry.maxPanX,
			geometry.maxPanX,
		)
		const nextPanY = clamp(
			dragRef.current.startPanY + dy,
			-geometry.maxPanY,
			geometry.maxPanY,
		)
		setPan({ x: nextPanX, y: nextPanY })
	}

	const handlePointerEnd = (event) => {
		if (dragRef.current?.pointerId === event.pointerId) {
			dragRef.current = null
		}
	}

	const handleApply = async () => {
		if (!sourceUrl || !targetWidth || !targetHeight || !onApply) return
		try {
			setApplying(true)
			const blob = await cropImageToBlob({
				sourceUrl,
				targetWidth,
				targetHeight,
				zoom,
				pan,
			})
			await onApply(blob)
		} catch (error) {
			alert(error.message || 'Could not crop image')
		} finally {
			setApplying(false)
		}
	}

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={title}
			size='lg'
			zIndex={zIndex}
			closeOnClickOutside={closeOnClickOutside}
			closeOnEscape={closeOnEscape}
		>
			<Stack spacing='md'>
				<Box
					ref={viewportRef}
					onPointerDown={handlePointerDown}
					onPointerMove={handlePointerMove}
					onPointerUp={handlePointerEnd}
					onPointerCancel={handlePointerEnd}
					style={{
						position: 'relative',
						width: '100%',
						aspectRatio: `${targetWidth} / ${targetHeight}`,
						overflow: 'hidden',
						borderRadius: 8,
						backgroundColor: '#111827',
						cursor: dragRef.current ? 'grabbing' : 'grab',
						touchAction: 'none',
					}}
				>
					{sourceUrl && geometry ? (
						<img src={sourceUrl} alt='Crop source' draggable={false} style={imageStyle} />
					) : (
						<Box
							style={{
								position: 'absolute',
								inset: 0,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<Text c='gray.4' size='sm'>
								Loading image...
							</Text>
						</Box>
					)}
				</Box>

				<Text size='xs' c='dimmed'>
					Drag image to reposition.
				</Text>

				<Box>
					<Text size='sm' fw={500} mb={6}>
						Zoom
					</Text>
					<Slider value={zoom} min={1} max={3} step={0.05} onChange={setZoom} />
				</Box>

				<Group position='right'>
					<Button
						variant='light'
						onClick={onClose}
						disabled={applying || processing}
					>
						Cancel
					</Button>
					<Button
						onClick={handleApply}
						loading={applying || processing}
						disabled={!sourceUrl || !geometry}
					>
						{applyLabel}
					</Button>
				</Group>
			</Stack>
		</Modal>
	)
}

export default ImageCropperModal
